import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

import { submitToWeb3Forms } from '../../lib/forms';

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitToWeb3Forms(formData, {
        formName: 'CTA Registration Form',
        subject: `New Interest Registration: ${formData.name}`
      });

      toast.success('Application submitted successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', school: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="cta-section" className="relative py-20 md:py-32 bg-gradient-to-br from-midnight via-richolive-dark to-midnight overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-dwarfpony/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block text-pragmatic text-sm font-semibold tracking-wider uppercase mb-4">
              Start Your Journey
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              We aim to make innovation truly
              <span className="text-dwarfpony-light"> democratic</span> and independent of privilege
            </h2>
            <p className="text-heavyrain text-lg leading-relaxed mb-8">
              Join thousands of students who have transformed their passion for STEM into real-world impact.
              Applications for FIRST Global 2027 are now open.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 hover:shadow-dwarfpony/40 transition-all group w-full sm:w-auto">
                  Apply Now
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-pragmatic/30 text-white hover:bg-pragmatic/10 px-8 py-6 text-lg w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-cream rounded-2xl p-8 shadow-2xl">
            <h3 className="text-deepwalnut font-bold text-xl mb-2">Register Your Interest</h3>
            <p className="text-heavyrain text-sm mb-6">Fill the form below and our team will reach out</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="cta-name-input"
                    className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony focus:ring-dwarfpony/20"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="cta-email-input"
                    className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony focus:ring-dwarfpony/20"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="cta-phone-input"
                    className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony focus:ring-dwarfpony/20"
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    name="school"
                    placeholder="School/Institution"
                    value={formData.school}
                    onChange={handleChange}
                    data-testid="cta-school-input"
                    className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony focus:ring-dwarfpony/20"
                  />
                </div>
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Tell us about your interest in robotics..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="cta-message-input"
                  className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony focus:ring-dwarfpony/20 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="cta-submit-btn"
                className="w-full bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold py-6 shadow-lg shadow-dwarfpony/25 transition-all"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
